<template>
    <div class="lost-item-dashboard-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة بلاغات المفقودات</h2>
          </div>
        </template>
  
        <el-alert
          title="متابعة بلاغات أولياء الأمور"
          type="info"
          description="هذه الشاشة تعرض جميع بلاغات المفقودات المرسلة من أولياء الأمور. يمكنك عرض تفاصيل كل بلاغ، الرد على ولي الأمر، وإغلاق البلاغ عند حل المشكلة."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-table :data="tickets" v-loading="loading" border style="width: 100%">
          <el-table-column prop="id" label="#" width="60" />
          <el-table-column prop="subject" label="الموضوع" />
          <el-table-column prop="student.name" label="الطالب" />
          <el-table-column prop="parent.name" label="ولي الأمر" />
          <el-table-column label="الحالة" width="120">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)">{{ row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="تاريخ الإنشاء" width="180">
              <template #default="{ row }">{{ formatDate(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="الإجراءات" width="150" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="openDetailsDialog(row)">عرض ومتابعة</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
  
      <!-- Dialog لعرض تفاصيل البلاغ والرد -->
      <el-dialog v-model="detailsDialogVisible" title="تفاصيل البلاغ" width="60%" top="5vh">
        <div v-if="selectedTicket" v-loading="detailsLoading">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="الطالب">{{ selectedTicket.student.name }}</el-descriptions-item>
            <el-descriptions-item label="ولي الأمر">{{ selectedTicket.parent.name }}</el-descriptions-item>
            <el-descriptions-item label="الموضوع">{{ selectedTicket.subject }}</el-descriptions-item>
            <el-descriptions-item label="الحالة">
              <el-tag :type="getStatusTagType(selectedTicket.status)">{{ selectedTicket.status }}</el-tag>
            </el-descriptions-item>
          </el-descriptions>
  
          <el-divider>سجل المحادثة</el-divider>
          <div class="chat-history">
            <div v-for="message in selectedTicket.messages" :key="message.id" class="chat-message" :class="{ 'admin-reply': message.sender.user_type === 'moder' }">
              <div class="message-sender">{{ message.sender.name }}</div>
              <div class="message-content">
                <p>{{ message.message }}</p>
                <el-image v-if="message.attachment_path" :src="getAttachmentUrl(message.attachment_path)" style="max-width: 200px; margin-top: 10px;" fit="cover" :preview-src-list="[getAttachmentUrl(message.attachment_path)]" />
              </div>
              <div class="message-timestamp">{{ formatDate(message.created_at) }}</div>
            </div>
          </div>
  
          <el-divider v-if="selectedTicket.status !== 'closed'">الرد على البلاغ</el-divider>
          <div v-if="selectedTicket.status !== 'closed'">
            <el-form :model="replyForm" ref="replyFormRef" label-position="top">
              <el-form-item label="رسالتك" prop="message">
                <el-input v-model="replyForm.message" type="textarea" :rows="3" />
              </el-form-item>
              <el-form-item label="إرفاق صورة (اختياري)">
                <el-upload action="#" :auto-upload="false" :on-change="handleImageChange" :show-file-list="false">
                  <el-button type="primary">اختر صورة</el-button>
                </el-upload>
                <div v-if="replyForm.attachment">{{ replyForm.attachment.name }}</div>
              </el-form-item>
            </el-form>
          </div>
        </div>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="detailsDialogVisible = false">إغلاق</el-button>
            <el-button type="danger" @click="closeTicket" :loading="isClosing" v-if="selectedTicket && selectedTicket.status !== 'closed'">إغلاق البلاغ</el-button>
            <el-button type="success" @click="sendReply" :loading="isReplying" v-if="selectedTicket && selectedTicket.status !== 'closed'">إرسال الرد</el-button>
          </span>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElMessageBox } from 'element-plus';
  import { Plus, Promotion } from '@element-plus/icons-vue';
  
  export default {
    name: 'LostItemDashboard',
    components: { Plus, Promotion },
    data() {
      return {
        api: null,
        loading: true,
        tickets: [],
        
        detailsDialogVisible: false,
        detailsLoading: false,
        selectedTicket: null,
        
        replyForm: this.getInitialReplyForm(),
        isReplying: false,
        isClosing: false,
      };
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchTickets();
    },
    methods: {
      getInitialReplyForm() {
        return { message: '', attachment: null };
      },
      async fetchTickets() {
        this.loading = true;
        try {
          const response = await this.api.get('/admin/lost-items');
          this.tickets = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب البلاغات.');
        } finally {
          this.loading = false;
        }
      },
      async openDetailsDialog(ticket) {
        this.detailsDialogVisible = true;
        this.detailsLoading = true;
        this.selectedTicket = null; // Reset first
        try {
          // We reuse the parent endpoint as it loads the messages relation
          const response = await this.api.get(`/parent/lost-items/${ticket.id}`);
          this.selectedTicket = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب تفاصيل البلاغ.');
          this.detailsDialogVisible = false;
        } finally {
          this.detailsLoading = false;
        }
      },
      handleImageChange(file) {
        this.replyForm.attachment = file.raw;
      },
      async sendReply() {
        if (!this.replyForm.message) {
          ElMessage.warning('الرجاء كتابة رسالة للرد.');
          return;
        }
        this.isReplying = true;
        const formData = new FormData();
        formData.append('message', this.replyForm.message);
        if (this.replyForm.attachment) {
          formData.append('attachment', this.replyForm.attachment);
        }
  
        try {
          // The reply endpoint handles both parent and admin replies
          const response = await this.api.post(`/parent/lost-items/${this.selectedTicket.id}/reply`, formData);
          this.selectedTicket.messages.push(response.data);
          this.replyForm = this.getInitialReplyForm();
          ElMessage.success('تم إرسال الرد بنجاح.');
        } catch (error) {
          ElMessage.error('فشل في إرسال الرد.');
        } finally {
          this.isReplying = false;
        }
      },
      closeTicket() {
        ElMessageBox.confirm('هل أنت متأكد من أنك تريد إغلاق هذا البلاغ؟ لن يتمكن ولي الأمر من الرد بعد الآن.', 'تأكيد الإغلاق', {
          confirmButtonText: 'نعم، قم بالإغلاق',
          cancelButtonText: 'إلغاء',
          type: 'warning',
        }).then(async () => {
          this.isClosing = true;
          try {
            await this.api.post(`/admin/lost-items/${this.selectedTicket.id}/close`);
            this.selectedTicket.status = 'closed';
            const index = this.tickets.findIndex(t => t.id === this.selectedTicket.id);
            if (index !== -1) {
              this.tickets[index].status = 'closed';
            }
            ElMessage.success('تم إغلاق البلاغ بنجاح.');
            this.detailsDialogVisible = false;
          } catch (error) {
            ElMessage.error('فشل في إغلاق البلاغ.');
          } finally {
            this.isClosing = false;
          }
        });
      },
      getStatusTagType(status) {
        if (status === 'open') return 'danger';
        if (status === 'pending') return 'warning';
        return 'success'; // closed
      },
      formatDate(dateString) {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString('ar-EG', options);
      },
      getAttachmentUrl(path) {
        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        if (!baseUrl) {
            console.error("VUE_APP_API_BASE_URL is not defined in your .env file.");
            return ''; // Return an empty string to avoid further errors
        }
        // Remove '/api' from the end of the base URL to get the root domain
        const rootUrl = baseUrl.replace(/\/api$/, '');
        return `${rootUrl}/storage/${path}`;
    }
    },
  };
  </script>
  
  <style scoped>
  .lost-item-dashboard-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .chat-history {
    max-height: 40vh;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid #eee;
    border-radius: 4px;
  }
  .chat-message {
    margin-bottom: 15px;
    padding: 10px;
    border-radius: 8px;
    background-color: #f4f4f5;
  }
  .chat-message.admin-reply {
    background-color: #e1f3d8;
  }
  .message-sender {
    font-weight: bold;
    margin-bottom: 5px;
  }
  .message-timestamp {
    font-size: 0.8em;
    color: #909399;
    text-align: right;
    margin-top: 5px;
  }
  </style>
  